import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Truck, Briefcase, FileText, ShoppingCart, Home, 
  HelpCircle, Edit, ChevronRight, MapPin, Calendar,
  X, Send, ArrowLeft, Car
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

// Import extracted form components
import LocalPickupForm from './forms/LocalPickupForm';
import LocalDeliveryForm from './forms/LocalDeliveryForm';
import SupplyRunForm from './forms/SupplyRunForm';
import HomeSupportForm from './forms/HomeSupportForm';
import GeneralAssistanceForm from './forms/GeneralAssistanceForm';
import CustomRequestForm from './forms/CustomRequestForm';
import BusinessCourierForm from './forms/BusinessCourierForm';

// Form type definitions - Non-medical services only
const FORM_TYPES = [
  {
    id: 'local_pickup',
    name: 'Local Pickup',
    description: 'Request pickup of items from a store or business',
    icon: Package,
    color: 'blue',
    category: 'courier'
  },
  {
    id: 'local_delivery',
    name: 'Local Delivery',
    description: 'Send items to another location',
    icon: Truck,
    color: 'green',
    category: 'courier'
  },
  {
    id: 'business_courier',
    name: 'Business Courier',
    description: 'Business-to-business package and document transport',
    icon: Briefcase,
    color: 'purple',
    category: 'courier'
  },
  {
    id: 'document_transport',
    name: 'Document Transport',
    description: 'Secure transport of documents and paperwork',
    icon: FileText,
    color: 'amber',
    category: 'courier'
  },
  {
    id: 'supply_run',
    name: 'Supply Run',
    description: 'Store runs and shopping errands',
    icon: ShoppingCart,
    color: 'cyan',
    category: 'errands'
  },
  {
    id: 'home_support',
    name: 'Home Support',
    description: 'Non-medical home assistance (cleaning, organizing, etc.)',
    icon: Home,
    color: 'pink',
    category: 'tasks'
  },
  {
    id: 'general_assistance',
    name: 'General Assistance',
    description: 'Everyday task help and support',
    icon: HelpCircle,
    color: 'indigo',
    category: 'tasks'
  },
  {
    id: 'custom',
    name: 'Custom Request',
    description: 'Special or unique service requests',
    icon: Edit,
    color: 'gray',
    category: 'custom'
  }
];

// Form component mapping
const FORM_COMPONENT_MAP = {
  local_pickup: LocalPickupForm,
  local_delivery: LocalDeliveryForm,
  supply_run: SupplyRunForm,
  home_support: HomeSupportForm,
  general_assistance: GeneralAssistanceForm,
  custom: CustomRequestForm,
  business_courier: BusinessCourierForm,
  document_transport: BusinessCourierForm
};

// Color utility functions
const getColorClasses = (color) => {
  const colors = {
    blue: 'bg-[#D4AF37]/10 border-[#D4AF37]/30 hover:border-blue-400',
    green: 'bg-[var(--success)]/10 border-[var(--success)]/30 hover:border-green-400',
    purple: 'bg-[#F4C2C2]/10 border-[#F4C2C2]/30 hover:border-purple-400',
    amber: 'bg-[var(--warning)]/10 border-[var(--warning)]/30 hover:border-amber-400',
    cyan: 'bg-cyan-50 border-cyan-200 hover:border-cyan-400',
    pink: 'bg-pink-50 border-pink-200 hover:border-pink-400',
    red: 'bg-[var(--error)]/10 border-[var(--error)]/30 hover:border-red-400',
    indigo: 'bg-indigo-50 border-indigo-200 hover:border-indigo-400',
    gray: 'bg-[var(--background-subtle)] border-[var(--border-primary)] hover:border-gray-400'
  };
  return colors[color] || colors.gray;
};

const getIconColorClass = (color) => {
  const colors = {
    blue: 'text-[#D4AF37]',
    green: 'text-[var(--success)]',
    purple: 'text-[#F4C2C2]',
    amber: 'text-amber-500',
    cyan: 'text-cyan-500',
    pink: 'text-pink-500',
    red: 'text-[var(--error)]',
    indigo: 'text-indigo-500',
    gray: 'text-[var(--text-muted)]'
  };
  return colors[color] || colors.gray;
};

const getStatusBadge = (status) => {
  const styles = {
    pending: 'bg-[var(--warning)]/15 text-yellow-700',
    reviewed: 'bg-[#D4AF37]/10 text-[#D4AF37]',
    approved: 'bg-[var(--success)]/15 text-green-700',
    converted: 'bg-[#F4C2C2]/15 text-purple-700',
    rejected: 'bg-[var(--error)]/15 text-red-700'
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-[var(--background-subtle)]'}`}>
      {status.toUpperCase()}
    </span>
  );
};

// Form Type Card Component
const FormTypeCard = ({ form, onClick }) => (
  <motion.button
    key={form.id}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`p-4 rounded-xl border-2 text-left transition-all ${getColorClasses(form.color)}`}
    data-testid={`form-type-${form.id}`}
  >
    <div className={`mb-3 ${getIconColorClass(form.color)}`}>
      {React.createElement(form.icon, { className: 'w-8 h-8' })}
    </div>
    <h4 className="font-bold">{form.name}</h4>
    <p className="text-xs text-[var(--text-muted)] mt-1">{form.description}</p>
    <div className="flex items-center gap-1 mt-3 text-xs text-[var(--primary)]">
      Start Request <ChevronRight className="w-3 h-3" />
    </div>
  </motion.button>
);

// Form Category Section Component
const FormCategorySection = ({ title, icon: Icon, iconColor, forms, onSelectForm, badge }) => (
  <div>
    <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
      <Icon className={`w-5 h-5 ${iconColor}`} />
      {title}
      {badge && (
        <span className="text-xs bg-[var(--success)]/15 text-green-700 px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {forms.map(form => (
        <FormTypeCard 
          key={form.id} 
          form={form} 
          onClick={() => onSelectForm(form)} 
        />
      ))}
    </div>
  </div>
);

// Submission Card Component
const SubmissionCard = ({ submission, formTypes }) => {
  const formType = formTypes.find(f => f.id === submission.form_type);
  
  return (
    <motion.div
      key={submission.id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card p-4 flex items-center justify-between"
      data-testid={`submission-${submission.id}`}
    >
      <div className="flex items-center gap-3">
        {formType && React.createElement(formType.icon, { 
          className: `w-6 h-6 ${getIconColorClass(formType?.color || 'gray')}` 
        })}
        <div>
          <p className="font-medium">{formType?.name || submission.form_type}</p>
          <p className="text-xs text-[var(--text-muted)]">
            {new Date(submission.created_at).toLocaleDateString()} • {submission.form_data?.preferred_date}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {submission.quoted_price && (
          <span className="text-[var(--success)] font-bold">${submission.quoted_price}</span>
        )}
        {getStatusBadge(submission.status)}
      </div>
    </motion.div>
  );
};

// Main Component
const ServiceRequestsTab = () => {
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMySubmissions();
  }, []);

  const fetchMySubmissions = async () => {
    try {
      const res = await api.get('/forms/submissions');
      setMySubmissions(res.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForm = async () => {
    if (!selectedFormType) return;
    
    setSubmitting(true);
    try {
      const endpoint = `/forms/${selectedFormType.id.replace(/_/g, '-')}`;
      await api.post(endpoint, {
        ...formData,
        form_type: selectedFormType.id
      });
      toast.success('Request submitted successfully!');
      setSelectedFormType(null);
      setFormData({});
      fetchMySubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormFields = () => {
    if (!selectedFormType) return null;
    
    const FormComponent = FORM_COMPONENT_MAP[selectedFormType.id];
    if (!FormComponent) return null;
    
    return <FormComponent formData={formData} setFormData={setFormData} />;
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading...</p>
      </div>
    );
  }

  // Form submission view
  if (selectedFormType) {
    return (
      <div data-testid="service-request-form">
        <button
          onClick={() => { setSelectedFormType(null); setFormData({}); }}
          className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text)] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to forms
        </button>
        
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-6">
            {React.createElement(selectedFormType.icon, { 
              className: `w-8 h-8 ${getIconColorClass(selectedFormType.color)}` 
            })}
            <div>
              <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">{selectedFormType.name}</h2>
              <p className="text-[var(--text-muted)]">{selectedFormType.description}</p>
            </div>
          </div>
          
          {/* SUV-Only Notice */}
          <div className="p-4 bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded-xl mb-4">
            <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
              <Car className="w-5 h-5" />
              SUV-Only Vehicle Policy
            </div>
            <p className="text-amber-600 text-sm">
              All courier and delivery services are completed using standard SUV or personal vehicles only. 
              Items must fit within SUV cargo capacity. No freight trucks or industrial hauling.
            </p>
          </div>
          
          {/* Service Area Notice */}
          <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl mb-6">
            <div className="flex items-center gap-2 text-[#D4AF37] font-medium mb-1">
              <MapPin className="w-5 h-5" />
              Service Area: Tarboro, NC 27886
            </div>
            <p className="text-[#D4AF37] text-sm">
              We serve Tarboro and all cities/towns within <strong>40 miles</strong>. 
              First 15 miles FREE, then $1.50/mile from 398 Tolbert Place.
            </p>
          </div>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSubmitForm(); }}>
            {renderFormFields()}
            
            <div>
              <label className="block text-sm font-medium mb-2 mt-6">Special Instructions</label>
              <textarea
                value={formData.special_instructions || ''}
                onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                className="w-full p-3 border rounded-xl min-h-[80px]"
                placeholder="Any additional notes or instructions"
              />
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setSelectedFormType(null); setFormData({}); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-primary"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Submit Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-requests-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
          <FileText className="w-6 h-6 text-[var(--primary)]" />
          Service Request Forms
        </h2>
        <p className="text-[var(--text-muted)] text-sm mt-1">
          Submit structured requests for our various services
        </p>
      </div>
      
      {/* SUV Policy Banner */}
      <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-[var(--warning)]/30 rounded-xl">
        <div className="flex items-center gap-3">
          <Car className="w-8 h-8 text-amber-600" />
          <div>
            <p className="font-bold text-amber-800">SUV-Only Vehicle Policy</p>
            <p className="text-sm text-amber-700">
              All transport and delivery services are completed with standard SUV or personal vehicles. 
              No freight trucks. Items must fit in SUV cargo area.
            </p>
          </div>
        </div>
      </div>

      {/* Form Type Categories */}
      <div className="space-y-6">
        <FormCategorySection
          title="Courier & Delivery"
          icon={Truck}
          iconColor="text-[var(--success)]"
          forms={FORM_TYPES.filter(f => f.category === 'courier')}
          onSelectForm={setSelectedFormType}
        />
        
        <FormCategorySection
          title="Errands & Shopping"
          icon={ShoppingCart}
          iconColor="text-cyan-600"
          forms={FORM_TYPES.filter(f => f.category === 'errands')}
          onSelectForm={setSelectedFormType}
        />
        
        <FormCategorySection
          title="Home Support & Private Concierge"
          icon={Home}
          iconColor="text-pink-600"
          forms={FORM_TYPES.filter(f => f.category === 'home')}
          onSelectForm={setSelectedFormType}
          badge="NON-MEDICAL"
        />
        
        <FormCategorySection
          title="Custom Requests"
          icon={Edit}
          iconColor="text-[var(--text-secondary)]"
          forms={FORM_TYPES.filter(f => f.category === 'custom')}
          onSelectForm={setSelectedFormType}
        />
      </div>

      {/* My Submissions */}
      <div className="mt-8">
        <h3 className="font-bold text-lg mb-4">My Submissions</h3>
        {mySubmissions.length === 0 ? (
          <div className="card p-8 text-center">
            <FileText className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-3" />
            <p className="text-[var(--text-muted)]">No submissions yet. Submit a request above!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {mySubmissions.map(sub => (
              <SubmissionCard 
                key={sub.id} 
                submission={sub} 
                formTypes={FORM_TYPES} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceRequestsTab;
